import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ControlBarButton } from '@m12s/component-library';
import { playRegular, signOut } from 'lib/icons';
import { UtilityMenu } from 'components/UtilityMenu';
import useOperatorLogout from 'components/LaborTicket/hooks/useOperatorLogout';
import { getMachine } from 'lib/selectors/getMachine';
import { getIsErpDriven } from 'lib/selectors/getIsErpDriven';
import Routes from 'lib/utils/routes';
import { CategorizeDowntime } from './CategorizeDowntime';
import {
  ControlBarResponsive,
  MobileIcon,
  ResponsiveText,
  Scrollable,
  SingleButtonScrollable,
} from '../styled';

/**
 * Control bar component for signed-in state with no active job.
 *
 * This component is rendered when:
 * - Operator is signed in
 * - No job is currently running
 *
 * Available actions:
 * - Start Job (only in non-ERP mode) - Navigate to job selection
 * - Categorize Downtime - Always available
 * - Sign Out (only in non-ERP mode) - Log out the current operator
 *
 * In ERP-driven mode, jobs are dispatched automatically, so start job
 * and sign out options are not available.
 *
 * @returns {JSX.Element} Control bar with job start, downtime categorization, and sign out options
 */
const SignedInNoJob = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const machine = useSelector(getMachine);
  const isErpDriven = useSelector(getIsErpDriven);
  const requestOperatorLogout = useOperatorLogout();

  // In ERP-driven mode, only CategorizeDowntime is shown (single button)
  const isSingleButton = isErpDriven;
  const Container = isSingleButton ? SingleButtonScrollable : Scrollable;

  return (
    <ControlBarResponsive id="job-control-bar">
      <UtilityMenu />
      <Container>
        {/* Start Job button - only shown in non-ERP mode */}
        {!isErpDriven && (
          <ControlBarButton
            id="job-control-start-job"
            onClick={() => {
              return history.push(Routes.machineIdSelectJobPath(machine.id));
            }}
          >
            <MobileIcon icon={playRegular} />
            <ResponsiveText
              full={t('startJob|full')}
              medium={t('startJob|medium')}
              short={t('startJob|short')}
            />
          </ControlBarButton>
        )}

        {/* Downtime categorization - always available */}
        <CategorizeDowntime history={history} machine={machine} />

        {/* Sign Out button - only shown in non-ERP mode */}
        {!isErpDriven && (
          <ControlBarButton
            id="signout"
            onClick={() => {
              return requestOperatorLogout();
            }}
          >
            <MobileIcon icon={signOut} />
            <ResponsiveText
              full={t('signout|full')}
              medium={t('signout|medium')}
              short={t('signout|short')}
            />
          </ControlBarButton>
        )}
      </Container>
    </ControlBarResponsive>
  );
};

export { SignedInNoJob };
